import { Check, X, Trash2, Send, Clock, Pencil, CheckSquare, Square, UserCheck, RotateCcw, AlertTriangle, DollarSign, ShieldCheck, Split } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { DIVISION_COLOR, fmtDate, fmtDateTime, formatIDR } from "@/lib/constants";

const STATUS_COLOR = {
  pending: "bg-amber-50 text-amber-800 border-amber-300",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-300",
  rejected: "bg-slate-100 text-slate-600 border-slate-300 line-through",
};
const STATUS_LABEL = { pending: "Menunggu", approved: "Disetujui", rejected: "Ditolak" };

function rowProjectLabel(pc) {
  if (pc.project_type === "variation_order") return `${pc.project_name} · VO ${pc.project_variation || ""}`.trim();
  if (pc.project_type === "working_order") return `${pc.project_name} · WO ${pc.project_wo_number || ""}`.trim();
  return pc.project_name || "Untitled";
}

export default function PettyCashRow({
  r,
  user,
  isBOD,
  isFinanceLead,
  pcPerms,
  reviewerOptions,
  selected,
  onToggleSelected,
  onOpenDetail,
  onOpenReview,
  onOpenEdit,
  onOpenTransfer,
  onOpenDelete,
  onAssignReviewer,
  onRestore,
  onReopen,
  onOpenActual,
  onOpenRealloc,
  onVerify,
  onQuickVerify,
}) {
  const isDeleted = !!r.deleted_at;
  const isOwnSubmission = r.user_id === user?.id;
  // 16-PETTY-CASH-APPROVAL-ACCESS-CONTROL-R1: fallback ke prop lama (isBOD/isFinanceLead)
  // hanya bila `pcPerms` tidak tersedia (backward-compat). Jika tersedia, GATE HANYA via pcPerms.
  const P = pcPerms || {};
  const canApprove = !!P.approve;
  const canReject = !!P.reject;
  const canEditOwn = !!P.edit_own;
  const canEditAll = !!P.edit_all;
  const canAssign = !!P.assign_approver;
  const canReopen = !!P.reopen;
  const canRestorePerm = !!P.restore;
  const canDeletePerm = !!P.delete;
  const canVerifyActual = !!P.verify_actual;
  // 149-cw-r180: tahapan Check pasca-verifikasi — perm granular `petty_cash.check`.
  const canCheckStage = !!P.check;
  const canConfirmTransfer = !!P.confirm_transfer;
  const canSelfApprove = !!P.self_approve;
  // Tombol Delete: owner boleh hapus pengajuan sendiri yg masih pending (hard delete via backend);
  // user lain butuh perm `delete`.
  const canDelete = !isDeleted && ((isOwnSubmission && r.status === "pending") || canDeletePerm);
  // Approve/Reject muncul terpisah — masing2 gated oleh permnya. Self-approve dicegah kecuali punya self_approve.
  const canReviewApprove = !isDeleted && canApprove && r.status === "pending" && (!isOwnSubmission || canSelfApprove);
  const canReviewReject = !isDeleted && canReject && r.status === "pending" && (!isOwnSubmission || canSelfApprove);
  // Edit muncul kalau owner + edit_own, atau other + edit_all. Status guard: hanya pending/rejected.
  const canEditItems = !isDeleted && r.status !== "approved" && !r.transferred_at && !r.is_verified &&
    ((isOwnSubmission && canEditOwn) || (!isOwnSubmission && canEditAll));
  // Reopen muncul untuk pengajuan yang sudah past-pending (approved/transferred/verified).
  const showReopen = !isDeleted && canReopen && (r.status === "approved" || r.transferred_at || r.is_verified);
  const canTransfer = !isDeleted && canConfirmTransfer && r.status === "approved" && !r.transfer_date;
  const canSelect = canReviewApprove || canReviewReject;
  const canRestore = canRestorePerm && isDeleted;
  const canAssignBtn = !isDeleted && canAssign && r.status === "pending";
  // Workflow: after transfer, submitter fills actual; then FAT/BOD verifies
  const isTransferred = !!r.transferred_at;
  const isActualFilled = !!r.actual_filled_at;
  // Status Verifikasi 1 dari backend (compute_v1_state) — item dibatalkan tidak dihitung.
  const isVerified = r.v1_complete ?? !!r.is_verified;
  const isAllCancelled = !!r.v1_all_cancelled;
  const pendingV1 = r.v1_pending_items ?? 0;
  const incompleteCount = r.v1_incomplete_items ?? 0;
  const canFillActual = !isDeleted && isOwnSubmission && isTransferred && !isVerified;
  const canVerify = !isDeleted && canVerifyActual && !isOwnSubmission && pendingV1 > 0 && !isVerified;
  const isActualIncomplete = isTransferred && !isVerified && incompleteCount > 0;
  // Row-level marker class untuk highlight visual (left-border kuning berkedip untuk yang perlu perhatian)
  const rowMarkerClass = isDeleted
    ? "border-l-4 border-l-rose-400 bg-rose-50/30"
    : isActualIncomplete
      ? "border-l-4 border-l-amber-500 bg-amber-50/40"
      : (pendingV1 > 0 && !isVerified)
        ? "border-l-4 border-l-sky-400 bg-sky-50/30"
        : isVerified
          ? "border-l-4 border-l-emerald-400"
          : "";

  return (
    <tr
      data-testid={`pc-row-${r.id}`}
      data-actual-incomplete={isActualIncomplete ? "1" : "0"}
      className={`border-t border-slate-100 hover:bg-slate-50 ${isDeleted ? "bg-rose-50/40 opacity-70" : selected ? "bg-amber-50" : ""} ${rowMarkerClass}`}
    >
      <td className="px-2 py-2 align-top">
        {canSelect ? (
          <button
            data-testid={`chk-pc-${r.id}`}
            onClick={() => onToggleSelected(r.id)}
            className="text-slate-500 hover:text-[#FF5A1F]"
          >{selected ? <CheckSquare size={14} className="text-[#FF5A1F]" /> : <Square size={14} />}</button>
        ) : null}
      </td>
      <td className="px-3 py-2 align-top">
        <div className="font-medium text-slate-800">{r.week_label}</div>
        {(r.period_start || r.period_end) && (
          <div className="text-[10px] text-slate-500 tabular-nums">
            {fmtDate(r.period_start)} → {fmtDate(r.period_end)}
          </div>
        )}
        <button
          data-testid={`btn-pc-detail-${r.id}`}
          onClick={() => onOpenDetail(r)}
          className="mt-1 text-[10px] text-[#FF5A1F] uppercase tracking-wider font-semibold hover:underline"
        >
          {r.items.length} item · view
        </button>
      </td>
      <td className="px-3 py-2 align-top text-slate-700 max-w-[220px] truncate">{rowProjectLabel(r)}</td>
      <td className="px-3 py-2 align-top">
        <div className="font-medium text-slate-800">{r.user_name}</div>
        <div className="mt-0.5 flex items-center gap-1 flex-wrap">
          <span className={`px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${DIVISION_COLOR[r.user_division] || "bg-slate-600 text-white"}`}>{r.user_division}</span>
          <span className="text-[10px] text-slate-500">Lv{r.user_level}</span>
        </div>
      </td>
      <td className="px-3 py-2 align-top text-right tabular-nums font-semibold text-slate-800">{formatIDR(r.total)}</td>
      <td className="px-3 py-2 align-top">
        <span className={`px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider border ${isDeleted ? "bg-rose-100 text-rose-800 border-rose-400" : STATUS_COLOR[r.status]}`}>
          {isDeleted ? "TERHAPUS" : STATUS_LABEL[r.status]}
        </span>
        {isDeleted && (
          <div className="mt-1 text-[10px] text-rose-700 space-y-0.5" data-testid={`pc-deleted-info-${r.id}`}>
            <div className="inline-flex items-center gap-0.5"><AlertTriangle size={9} /> by {r.deleted_by_name || "-"}</div>
            <div className="text-slate-500 tabular-nums">{fmtDateTime ? fmtDateTime(r.deleted_at) : String(r.deleted_at).slice(0, 16).replace("T", " ")}</div>
            {r.delete_reason && <div className="italic text-slate-600">"{r.delete_reason}"</div>}
          </div>
        )}
          {isBOD && !isDeleted && r.status === "pending" && r.assigned_reviewer_name && (
            <div className="mt-1 text-[10px] text-[#FF5A1F] font-semibold inline-flex items-center gap-0.5" data-testid={`pc-assigned-${r.id}`} title={`Ditugaskan oleh ${r.assigned_by_name || "-"}`}>
              <Clock size={9} /> ditugaskan ke {r.assigned_reviewer_name.split(" ")[0]}
            </div>
          )}
          {!isDeleted && r.status === "pending" && !r.assigned_reviewer_name && (
            <div className="mt-1 text-[10px] text-slate-500 inline-flex items-center gap-0.5">
              <Clock size={9} /> menunggu review (semua approver berwenang)
            </div>
          )}
        {!isDeleted && r.review_note && (
          <div className="mt-1 text-[10px] text-slate-500 italic">{r.review_note}</div>
        )}
        {!isDeleted && r.reviewed_by_name && (
          <div className="mt-0.5 text-[10px] text-slate-400">by {r.reviewed_by_name}</div>
        )}
      </td>
      <td className="px-3 py-2 align-top text-[10px]">
        {r.status !== "approved" ? (
          <span className="text-slate-400">-</span>
        ) : r.transfer_date ? (
          <>
            <div className="tabular-nums text-emerald-700 font-semibold">{fmtDate(r.transfer_date)}</div>
            {r.transfer_amount != null && r.transfer_amount !== r.total && (
              <div className="tabular-nums text-emerald-600">{formatIDR(r.transfer_amount)}</div>
            )}
            <div className="mt-0.5 text-slate-600" data-testid={`pc-transfer-source-${r.id}`}>
              Sumber: <span className="font-semibold">{r.transfer_source?.type === "cash" ? "Cash" : r.transfer_source?.type === "staff_bank" ? `Staff · ${r.transfer_source.owner_name || "-"}` : r.transfer_source?.type === "company_bank" ? "Rekening Perusahaan" : "—"}</span>
              {r.transfer_source?.bank_name && <div className="text-[9px] text-slate-500 font-mono">{r.transfer_source.bank_name} — {r.transfer_source.account_no}</div>}
            </div>
            <div className="text-slate-500">by {r.transferred_by_name}</div>
            {r.transfer_note && <div className="text-slate-500 italic">{r.transfer_note}</div>}
            {/* Actual expense state */}
            {isVerified ? (
              <div className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 border border-emerald-400 bg-emerald-100 text-emerald-800 font-semibold uppercase tracking-wider" data-testid={`pc-verified-${r.id}`}>
                <ShieldCheck size={10} /> {isAllCancelled ? "Selesai · semua item dibatalkan" : "Verifikasi 1 selesai"}
              </div>
            ) : isActualIncomplete ? (
              <div className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 border-2 border-amber-500 bg-amber-100 text-amber-900 font-bold uppercase tracking-wider animate-pulse" data-testid={`pc-actual-incomplete-${r.id}`} title={`${incompleteCount} item belum diisi aktualnya`}>
                <AlertTriangle size={11} /> Aktual belum lengkap · {incompleteCount} item
              </div>
            ) : pendingV1 > 0 ? (
              <div className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 border border-sky-400 bg-sky-50 text-sky-800 font-semibold uppercase tracking-wider" data-testid={`pc-actual-pending-${r.id}`} title={`Aktual: ${formatIDR(r.actual_amount || 0)} pada ${fmtDate(r.actual_transaction_date)}`}>
                <Clock size={10} /> Menunggu Verifikasi 1 · {pendingV1} item
              </div>
            ) : isActualFilled ? (
              <div className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 border border-amber-400 bg-amber-50 text-amber-800 font-semibold uppercase tracking-wider" data-testid={`pc-actual-revision-${r.id}`}>
                <AlertTriangle size={10} /> Menunggu perbaikan pengaju
              </div>
            ) : (
              <div className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 border-2 border-amber-500 bg-amber-100 text-amber-900 font-bold uppercase tracking-wider animate-pulse" data-testid={`pc-actual-needed-${r.id}`}>
                <AlertTriangle size={11} /> Belum isi aktual
              </div>
            )}
            {isActualFilled && (
              <div className="mt-1 text-slate-600 tabular-nums">
                Aktual: <span className="font-semibold text-slate-800">{formatIDR(r.actual_amount || 0)}</span> · {fmtDate(r.actual_transaction_date)}
              </div>
            )}
          </>
        ) : (
          <span className="inline-flex items-center gap-1 text-amber-600"><Clock size={11} /> menunggu transfer</span>
        )}
      </td>
      {/* 149-cw-r180: kolom Check — tahapan TERPISAH setelah verifikasi.
          Bisa diklik hanya kalau sudah terverifikasi & user punya perm `check`.
          Setelah di-check: tampil nama pengecek + tanggalnya. */}
      <td className="px-2 py-2 align-top text-center" data-testid={`pc-check-${r.id}`}>
        {(() => {
          const isChecked = !!r.checked_at;
          if (isDeleted || !isTransferred || !isActualFilled || isAllCancelled) {
            return <span className="text-slate-300 select-none">—</span>;
          }
          if (isChecked) {
            return (
              <div data-testid={`pc-check-done-${r.id}`} className="inline-flex flex-col items-center gap-0.5">
                <span className="inline-flex items-center justify-center w-6 h-6 border border-emerald-500 bg-emerald-100 text-emerald-700">
                  <Check size={14} strokeWidth={3} />
                </span>
                <span className="text-[8px] leading-tight text-emerald-700 font-semibold max-w-[90px] truncate" title={r.checked_by_name || ""}>
                  {r.checked_by_name || "—"}
                </span>
                <span className="text-[8px] leading-tight text-slate-400 tabular-nums">{fmtDate(r.checked_at)}</span>
              </div>
            );
          }
          if (!isVerified) {
            return (
              <span
                className="inline-flex items-center justify-center w-6 h-6 border border-slate-300 bg-slate-50 text-slate-400"
                title="Menunggu Verifikasi 1 selesai — Check hanya bisa setelah Verifikasi 1"
                data-testid={`pc-check-wait-verify-${r.id}`}
              ><Clock size={12} /></span>
            );
          }
          if (canCheckStage && !isOwnSubmission) {
            return (
              <button
                type="button"
                data-testid={`pc-check-btn-${r.id}`}
                onClick={() => onQuickVerify?.(r)}
                className="inline-flex items-center justify-center w-6 h-6 border-2 border-slate-400 bg-white text-transparent hover:border-emerald-600 hover:bg-emerald-50 hover:text-emerald-600 transition-colors"
                title="Check pengeluaran yang sudah lolos Verifikasi 1 — nama & tanggal Anda akan tercatat"
              ><Check size={14} strokeWidth={3} /></button>
            );
          }
          return (
            <span
              className="inline-flex items-center justify-center w-6 h-6 border border-amber-400 bg-amber-50 text-amber-600"
              title="Verifikasi 1 selesai — menunggu Check oleh user berwenang (perm Check pasca-verifikasi)"
              data-testid={`pc-check-wait-${r.id}`}
            ><Clock size={12} /></span>
          );
        })()}
      </td>
      <td className="px-3 py-2 align-top">
        <div className="flex justify-end items-center gap-1 flex-wrap">
          {canRestore && (
            <button
              data-testid={`btn-restore-pc-${r.id}`}
              onClick={() => onRestore(r)}
              className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 hover:bg-emerald-50 border border-emerald-300 inline-flex items-center gap-1"
            ><RotateCcw size={12} /> Restore</button>
          )}
          {canAssignBtn && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid={`btn-assign-pc-${r.id}`}
                  title="Tugaskan approver (koordinasi/notif — tidak memberi permission baru)"
                  className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-[#FF5A1F] hover:bg-orange-50 border border-[#FF5A1F] inline-flex items-center gap-1"
                ><UserCheck size={12} /> {r.assigned_reviewer_name ? "Re-assign" : "Assign"}</button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-none w-56">
                <DropdownMenuLabel className="text-[10px] overline text-slate-500">
                  Tugaskan reviewer
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {reviewerOptions.length === 0 && (
                  <div className="px-2 py-2 text-xs text-slate-400">Tidak ada FAT-L2 tersedia</div>
                )}
                {reviewerOptions.map((rv) => (
                  <DropdownMenuItem
                    key={rv.id}
                    data-testid={`opt-reviewer-${r.id}-${rv.id}`}
                    onClick={() => onAssignReviewer(r.id, rv.id)}
                    className="text-xs"
                  >
                    <UserCheck size={11} className="mr-2 text-[#FF5A1F]" />
                    {rv.name}
                    {r.assigned_reviewer_id === rv.id && <Check size={12} className="ml-auto text-emerald-600" />}
                  </DropdownMenuItem>
                ))}
                {r.assigned_reviewer_id && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      data-testid={`opt-reviewer-clear-${r.id}`}
                      onClick={() => onAssignReviewer(r.id, null)}
                      className="text-xs text-rose-600"
                    >
                      <X size={11} className="mr-2" /> Batalkan assignment
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {canReviewApprove && (
            <button
              data-testid={`btn-approve-pc-${r.id}`}
              onClick={() => onOpenReview(r, "approve")}
              className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 hover:bg-emerald-50 border border-emerald-300 inline-flex items-center gap-1"
            ><Check size={12} /> Approve</button>
          )}
          {canReviewReject && (
            <button
              data-testid={`btn-reject-pc-${r.id}`}
              onClick={() => onOpenReview(r, "reject")}
              className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-rose-700 hover:bg-rose-50 border border-rose-300 inline-flex items-center gap-1"
            ><X size={12} /> Reject</button>
          )}
          {showReopen && (
            <button
              data-testid={`btn-reopen-pc-${r.id}`}
              onClick={() => onReopen && onReopen(r)}
              title="Kembalikan ke Pending untuk approval ulang"
              className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-violet-700 hover:bg-violet-50 border border-violet-300 inline-flex items-center gap-1"
            ><RotateCcw size={12} /> Reopen</button>
          )}
          {canEditItems && (
            <button
              data-testid={`btn-edit-pc-${r.id}`}
              onClick={() => onOpenEdit(r)}
              className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-amber-800 hover:bg-amber-50 border border-amber-400 inline-flex items-center gap-1"
            ><Pencil size={12} /> Edit</button>
          )}
          {canTransfer && (
            <button
              data-testid={`btn-transfer-pc-${r.id}`}
              onClick={() => onOpenTransfer(r)}
              className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-white bg-[#0B1D3A] hover:bg-[#132a5b] inline-flex items-center gap-1"
            ><Send size={12} /> Transfer</button>
          )}
          {canVerify && (
            <button
              data-testid={`btn-verify-pc-${r.id}`}
              onClick={() => onVerify(r)}
              className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-white bg-emerald-600 hover:bg-emerald-700 inline-flex items-center gap-1"
              title="Buka detail untuk Verifikasi 1 / reject setiap item"
            ><ShieldCheck size={12} /> Verifikasi 1</button>
          )}
          {canFillActual && !isActualFilled && (
            <button
              data-testid={`btn-actual-pc-${r.id}`}
              onClick={() => onOpenActual(r)}
              className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-white bg-amber-600 hover:bg-amber-700 inline-flex items-center gap-1"
              title="Isi realisasi setelah menerima transfer"
            ><DollarSign size={12} /> Isi Aktual</button>
          )}
          {/* 149-cw-r185: Pengalihan Kas — pengaju dapat mengubah item pengajuan
              menjadi banyak item realisasi setelah transfer, sebelum verifikasi. */}
          {canFillActual && onOpenRealloc && (
            <button
              data-testid={`btn-realloc-pc-${r.id}`}
              onClick={() => onOpenRealloc(r)}
              className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-amber-800 border border-amber-500 bg-amber-50 hover:bg-amber-100 inline-flex items-center gap-1"
              title="Pengalihan Kas — ubah item realisasi menjadi lebih dari 1"
            ><Split size={12} /> Pengalihan</button>
          )}
          {canDelete && (
            <button
              data-testid={`btn-delete-pc-${r.id}`}
              onClick={() => onOpenDelete(r)}
              className="px-2 py-1 text-[10px] text-slate-500 hover:text-rose-600 hover:bg-rose-50 inline-flex items-center gap-1"
            ><Trash2 size={12} /></button>
          )}
        </div>
      </td>
    </tr>
  );
}
